import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("adminToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      if (window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin/login") {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const adminLogin = (data) => api.post("/admin/login", data);
export const verifyAdmin = () => api.get("/admin/verify");

// Matches
export const getMatches = (params) => api.get("/matches", { params });
export const getAllMatches = () => api.get("/matches/all");
export const getMatch = (id) => api.get(`/matches/${id}`);
export const createMatch = (data) => api.post("/matches", data);
export const updateMatch = (id, data) => api.put(`/matches/${id}`, data);
export const deleteMatch = (id) => api.delete(`/matches/${id}`);
export const updateLiveScore = (id, homeScore, awayScore, status = "live") => 
  api.put(`/matches/${id}/live-score?home_score=${homeScore}&away_score=${awayScore}&status=${status}`);

// News
export const getNews = (params) => api.get("/news", { params });
export const getNewsArticle = (id) => api.get(`/news/${id}`);
export const createNews = (data) => api.post("/news", data);
export const updateNews = (id, data) => api.put(`/news/${id}`, data);
export const deleteNews = (id) => api.delete(`/news/${id}`);

// Players
export const getPlayers = (params) => api.get("/players", { params });
export const getPlayer = (id) => api.get(`/players/${id}`);
export const createPlayer = (data) => api.post("/players", data);
export const updatePlayer = (id, data) => api.put(`/players/${id}`, data);
export const deletePlayer = (id) => api.delete(`/players/${id}`);
export const updatePlayerStats = (id, stats) => api.put(`/players/${id}/stats`, stats);
export const getTopScorers = (params) => api.get("/players/top-scorers", { params });
export const getTopAssists = (params) => api.get("/players/top-assists", { params });

// Team Staff
export const getTeamStaff = (params) => api.get("/team-staff", { params });
export const getTeamStaffById = (id) => api.get(`/team-staff/${id}`);
export const createTeamStaff = (data) => api.post("/team-staff", data);
export const updateTeamStaff = (id, data) => api.put(`/team-staff/${id}`, data);
export const deleteTeamStaff = (id) => api.delete(`/team-staff/${id}`);

// File Upload
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
};
export const deleteFile = (filename) => api.delete(`/upload/${filename}`);

// Standings
export const getStandings = (params) => api.get("/standings", { params });
export const createStanding = (data) => api.post("/standings", data);
export const updateStanding = (id, data) => api.put(`/standings/${id}`, data);
export const deleteStanding = (id) => api.delete(`/standings/${id}`);

// Contacts
export const getContacts = () => api.get("/contacts");
export const createContact = (data) => api.post("/contacts", data);
export const markContactRead = (id) => api.put(`/contacts/${id}/read`);
export const deleteContact = (id) => api.delete(`/contacts/${id}`);

// Seed Data
export const seedData = () => api.post("/seed");

// Team Categories
export const getTeamCategories = () => api.get("/team-categories");
export const createTeamCategory = (data) => api.post("/team-categories", data);
export const updateTeamCategory = (id, data) => api.put(`/team-categories/${id}`, data);
export const deleteTeamCategory = (id) => api.delete(`/team-categories/${id}`);

// News Categories
export const getNewsCategories = () => api.get("/news-categories");
export const createNewsCategory = (data) => api.post("/news-categories", data);
export const updateNewsCategory = (id, data) => api.put(`/news-categories/${id}`, data);
export const deleteNewsCategory = (id) => api.delete(`/news-categories/${id}`);

// Sponsors
export const getSponsors = (params) => api.get("/sponsors", { params });
export const getAllSponsors = () => api.get("/sponsors/all");
export const createSponsor = (data) => api.post("/sponsors", data);
export const updateSponsor = (id, data) => api.put(`/sponsors/${id}`, data);
export const deleteSponsor = (id) => api.delete(`/sponsors/${id}`);

// Shop
export const getShopProducts = (params) => api.get("/shop", { params });
export const getShopProductsAdmin = () => api.get("/shop/admin/all");  // Admin endpoint with cost data
export const createShopProduct = (data) => api.post("/shop", data);
export const updateShopProduct = (id, data) => api.put(`/shop/${id}`, data);
export const deleteShopProduct = (id) => api.delete(`/shop/${id}`);

// Orders
export const getOrders = (params) => api.get("/orders", { params });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post("/orders", data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const getOrderStats = () => api.get("/orders/stats/summary");

// Admin Users
export const getAdminUsers = () => api.get("/admin/users");
export const createAdminUser = (data) => api.post("/admin/users", data);
export const updateAdminUser = (id, data) => api.put(`/admin/users/${id}`, data);
export const deleteAdminUser = (id) => api.delete(`/admin/users/${id}`);
export const getCurrentAdmin = () => api.get("/admin/me");
export const changePassword = (oldPassword, newPassword) => 
  api.put(`/admin/change-password?old_password=${oldPassword}&new_password=${newPassword}`);

// Vacancies
export const getVacancies = (params) => api.get("/vacancies", { params });
export const getAllVacancies = () => api.get("/vacancies/all");
export const getVacancy = (id) => api.get(`/vacancies/${id}`);
export const createVacancy = (data) => api.post("/vacancies", data);
export const updateVacancy = (id, data) => api.put(`/vacancies/${id}`, data);
export const deleteVacancy = (id) => api.delete(`/vacancies/${id}`);

// Applications (Sollicitaties)
export const getApplications = (params) => api.get("/applications", { params });
export const getApplication = (id) => api.get(`/applications/${id}`);
export const createApplication = (data) => api.post("/applications", data);
export const updateApplication = (id, data) => api.put(`/applications/${id}`, data);
export const deleteApplication = (id) => api.delete(`/applications/${id}`);

// Gallery (Sport Eldorado)
export const getGallery = (params) => api.get("/gallery", { params });
export const getAllGallery = () => api.get("/gallery/all");
export const createGalleryImage = (data) => api.post("/gallery", data);
export const updateGalleryImage = (id, data) => api.put(`/gallery/${id}`, data);
export const deleteGalleryImage = (id) => api.delete(`/gallery/${id}`);

// Activities (Sport Eldorado Kalender)
export const getActivities = (params) => api.get("/activities", { params });
export const getAllActivities = () => api.get("/activities/all");
export const getActivity = (id) => api.get(`/activities/${id}`);
export const createActivity = (data) => api.post("/activities", data);
export const updateActivity = (id, data) => api.put(`/activities/${id}`, data);
export const deleteActivity = (id) => api.delete(`/activities/${id}`);

// Sport Eldorado Team
export const getEldoradoTeam = (params) => api.get("/eldorado-team", { params });
export const getAllEldoradoTeam = () => api.get("/eldorado-team/all");
export const createEldoradoTeamMember = (data) => api.post("/eldorado-team", data);
export const updateEldoradoTeamMember = (id, data) => api.put(`/eldorado-team/${id}`, data);
export const deleteEldoradoTeamMember = (id) => api.delete(`/eldorado-team/${id}`);

// Activity Registrations
export const createActivityRegistration = (data) => api.post("/activity-registrations", data);
export const getActivityRegistrations = (params) => api.get("/activity-registrations", { params });
export const deleteActivityRegistration = (id) => api.delete(`/activity-registrations/${id}`);

// Teams
export const getTeams = (params) => api.get("/teams", { params });
export const getAllTeams = () => api.get("/teams/all");
export const createTeam = (data) => api.post("/teams", data);
export const updateTeam = (id, data) => api.put(`/teams/${id}`, data);
export const deleteTeam = (id) => api.delete(`/teams/${id}`);

// Locations
export const getLocations = () => api.get("/locations");
export const getAllLocations = () => api.get("/locations/all");
export const createLocation = (data) => api.post("/locations", data);
export const updateLocation = (id, data) => api.put(`/locations/${id}`, data);
export const deleteLocation = (id) => api.delete(`/locations/${id}`);

// Competitions
export const getCompetitions = () => api.get("/competitions");
export const getAllCompetitions = () => api.get("/competitions/all");
export const createCompetition = (data) => api.post("/competitions", data);
export const updateCompetition = (id, data) => api.put(`/competitions/${id}`, data);
export const deleteCompetition = (id) => api.delete(`/competitions/${id}`);

// Newsletter
export const subscribeNewsletter = (data) => api.post("/newsletter/subscribe", data);
export const getNewsletterSubscribers = () => api.get("/newsletter/subscribers");
export const deleteNewsletterSubscriber = (id) => api.delete(`/newsletter/subscribers/${id}`);
export const getNewsletters = () => api.get("/newsletters");
export const createNewsletter = (data) => api.post("/newsletters", data);
export const updateNewsletter = (id, data) => api.put(`/newsletters/${id}`, data);
export const deleteNewsletter = (id) => api.delete(`/newsletters/${id}`);
export const sendNewsletter = (id) => api.post(`/newsletters/${id}/send`);

// Player Registrations
export const createRegistration = (data) => api.post("/registrations", data);
export const getRegistrations = (params) => api.get("/registrations", { params });
export const getRegistrationStats = () => api.get("/registrations/stats");
export const updateRegistration = (id, data) => api.put(`/registrations/${id}`, data);
export const deleteRegistration = (id) => api.delete(`/registrations/${id}`);

export default api;
